import { CurrencyPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BillStorageService, StoredBillSummary } from './bill-storage.service';

@Component({
  selector: 'app-bill-history',
  standalone: true,
  imports: [CurrencyPipe],
  templateUrl: './bill-history.component.html',
  styleUrl: './bill-history.component.scss',
})
export class BillHistoryComponent implements OnInit {
  private readonly storage = inject(BillStorageService);
  private readonly router = inject(Router);
  readonly bills = signal<StoredBillSummary[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');
  readonly deletingBillIds = signal<ReadonlySet<string>>(new Set());

  ngOnInit(): void { void this.refresh(); }

  async refresh(): Promise<void> {
    this.loading.set(true); this.error.set('');
    try { this.bills.set(await this.storage.list()); }
    catch (error) { this.error.set(error instanceof Error ? error.message : 'Could not load saved bills.'); }
    finally { this.loading.set(false); }
  }

  openBill(id: string): void { void this.router.navigate(['/bill-splitter', id]); }

  openBillFromKeyboard(event: Event, id: string): void {
    if (event.target instanceof HTMLElement && event.target.closest('button')) return;
    this.openBill(id);
  }

  async openSource(event: MouseEvent, bill: StoredBillSummary): Promise<void> {
    event.stopPropagation();
    if (!bill.sourcePath) return;
    // Open synchronously to retain the user's click gesture, then navigate the
    // blank tab to the short-lived private Storage URL once it is available.
    const tab = window.open('about:blank', '_blank');
    if (!tab) { this.error.set('Your browser blocked the file window. Allow pop-ups and try again.'); return; }
    tab.opener = null;
    try { tab.location.replace(await this.storage.sourceUrl(bill.sourcePath)); }
    catch (error) {
      tab.close();
      this.error.set(error instanceof Error ? error.message : 'Could not open the original bill file.');
    }
  }

  sourceKind(bill: StoredBillSummary): 'image' | 'pdf' | 'text' | 'document' {
    const mime = bill.sourceMime?.toLowerCase();
    if (mime?.startsWith('image/')) return 'image';
    if (mime === 'application/pdf') return 'pdf';
    if (mime?.startsWith('text/')) return 'text';

    const extension = bill.sourceFileName?.split('.').at(-1)?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'avif'].includes(extension ?? '')) return 'image';
    if (extension === 'pdf') return 'pdf';
    if (['txt', 'csv', 'json', 'md'].includes(extension ?? '')) return 'text';
    return 'document';
  }

  async deleteBill(event: MouseEvent, bill: StoredBillSummary): Promise<void> {
    event.stopPropagation();
    if (this.deletingBillIds().has(bill.id)) return;
    const originalFile = bill.sourcePath ? ' and its original file' : '';
    if (!window.confirm(`Delete ${bill.merchant || 'this bill'}${originalFile}? This cannot be undone.`)) return;

    this.error.set('');
    this.deletingBillIds.update((ids) => new Set(ids).add(bill.id));
    try {
      await this.storage.delete(bill.id, bill.sourcePath);
      this.bills.update((bills) => bills.filter((entry) => entry.id !== bill.id));
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Could not delete this bill.');
    } finally {
      this.deletingBillIds.update((ids) => {
        const next = new Set(ids);
        next.delete(bill.id);
        return next;
      });
    }
  }
}
